/**
 * Error handling utilities for type-safe error message extraction.
 */

import { ApiError } from '../api/apiBase';

// ============================================
// Error Code Types
// ============================================

/** Standardized error codes for consistent error handling */
export type ErrorCode =
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'RATE_LIMITED'
  | 'VALIDATION_ERROR'
  | 'NETWORK_ERROR'
  | 'SERVER_ERROR'
  | 'UNKNOWN_ERROR';

/** Structured application error with code and message */
export interface AppError {
  code: ErrorCode;
  message: string;
  statusCode?: number;
  details?: string;
}

// ============================================
// Error Type Interfaces
// ============================================

export interface AxiosErrorLike {
  response?: {
    status?: number;
    data?: {
      error?: string;
      message?: string;
      code?: string;
    };
  };
  message?: string;
  code?: string;
}

// ============================================
// Type Guards
// ============================================

/**
 * Type guard for Axios-like error objects
 */
export function isAxiosErrorLike(error: unknown): error is AxiosErrorLike {
  return (
    typeof error === 'object' &&
    error !== null &&
    ('response' in error || 'message' in error)
  );
}

/**
 * Type guard for Axios error with specific HTTP status
 */
export function isAxiosErrorWithStatus(
  error: unknown,
  status: number
): error is AxiosErrorLike {
  return isAxiosErrorLike(error) && error.response?.status === status;
}

/**
 * Type guard for unauthorized (401) errors
 */
export function isUnauthorizedError(error: unknown): error is AxiosErrorLike {
  return isAxiosErrorWithStatus(error, 401);
}

/**
 * Type guard for forbidden (403) errors
 */
export function isForbiddenError(error: unknown): error is AxiosErrorLike {
  return isAxiosErrorWithStatus(error, 403);
}

/**
 * Type guard for not found (404) errors
 */
export function isNotFoundError(error: unknown): error is AxiosErrorLike {
  return isAxiosErrorWithStatus(error, 404);
}

/**
 * Type guard for rate limit (429) errors
 */
export function isRateLimitError(error: unknown): error is AxiosErrorLike {
  return isAxiosErrorWithStatus(error, 429);
}

/**
 * Type guard for network errors (no response)
 */
export function isNetworkError(error: unknown): boolean {
  return (
    isAxiosErrorLike(error) &&
    error.response === undefined &&
    (error.code === 'ERR_NETWORK' || Boolean(error.message?.includes('Network Error')))
  );
}

// ============================================
// Error Extraction Functions
// ============================================

/**
 * Shown when Switchboard never answered: no response, a timeout, a gateway
 * 502/503/504 without a JSON body, or the public transport's NETWORK /
 * BAD_RESPONSE (the CDN's index.html in place of JSON).
 */
export const SERVER_UNREACHABLE_MESSAGE =
  "Switchboard isn't responding. Check your connection and try again.";

/** Shown for a 5xx the server answered without saying why. */
export const SERVER_FAULT_MESSAGE = 'Something went wrong on our side.';

const DEFAULT_FALLBACK = 'Something went wrong. Please try again.';

/** Axios `code`s that mean no response ever arrived. */
const NO_RESPONSE_CODES = new Set(['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT', 'ECONNREFUSED']);
const GATEWAY_STATUSES = new Set([502, 503, 504]);

/** The message the server put in its JSON body, in either backend shape. */
function serverBodyMessage(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null || !('response' in error)) return undefined;
  const data = (error as { response?: { data?: unknown } }).response?.data;
  if (typeof data !== 'object' || data === null) return undefined;
  const { error: bodyError, message } = data as { error?: unknown; message?: unknown };
  if (typeof bodyError === 'string' && bodyError) return bodyError;
  if (typeof bodyError === 'object' && bodyError !== null) {
    const nested = (bodyError as { message?: unknown }).message;
    if (typeof nested === 'string' && nested) return nested;
  }
  if (typeof message === 'string' && message) return message;
  return undefined;
}

/** Copy for a status the server answered without a usable body. */
function statusMessage(status: number | undefined, fallback: string): string {
  if (status === undefined) return SERVER_UNREACHABLE_MESSAGE;
  if (GATEWAY_STATUSES.has(status)) return SERVER_UNREACHABLE_MESSAGE;
  if (status >= 500) return SERVER_FAULT_MESSAGE;
  return fallback;
}

/**
 * The one sentence the UI shows for any failure. Precedence:
 *   1. the server's own `error` / `message` body, as is (keeps details such
 *      as a 502 INVITE_EMAIL_FAILED reason);
 *   2. a plain non-axios `Error`'s message (what mocked clients reject with);
 *   3. no response, a timeout, or a 502/503/504 without a body →
 *      SERVER_UNREACHABLE_MESSAGE;
 *   4. any other 5xx without a body → SERVER_FAULT_MESSAGE;
 *   5. `fallback` — never axios's raw "Request failed with status code …".
 */
export function getErrorMessage(error: unknown, fallback = DEFAULT_FALLBACK): string {
  const body = serverBodyMessage(error);
  if (body) return body;

  // The public-page transport (apiBase). Its message is the server's own
  // text unless the body carried none, in which case it equals the code.
  if (error instanceof ApiError) {
    if (error.code === 'NETWORK' || error.code === 'TIMEOUT' || error.code === 'BAD_RESPONSE') return SERVER_UNREACHABLE_MESSAGE;
    if (error.message && error.message !== error.code) return error.message;
    return error.status !== undefined ? statusMessage(error.status, fallback) : fallback;
  }

  if (typeof error === 'object' && error !== null) {
    const e = error as AxiosErrorLike & { isAxiosError?: boolean };
    const isTransport =
      e.isAxiosError === true || 'response' in e || (e.code !== undefined && NO_RESPONSE_CODES.has(e.code));
    if (isTransport) {
      if (e.code === 'ERR_CANCELED') return fallback;
      if (e.response === undefined) {
        return NO_RESPONSE_CODES.has(e.code ?? '') || e.isAxiosError === true
          ? SERVER_UNREACHABLE_MESSAGE
          : fallback;
      }
      return statusMessage(e.response.status, fallback);
    }
  }

  // `fetch` rejects with a TypeError when the request never got a response.
  if (error instanceof TypeError && /fetch|network|load failed/i.test(error.message)) {
    return SERVER_UNREACHABLE_MESSAGE;
  }

  if (error instanceof Error) {
    return error.message || fallback;
  }

  if (typeof error === 'string') {
    return error;
  }

  return fallback;
}

/**
 * True when Switchboard never answered: the same failures getErrorMessage
 * words as SERVER_UNREACHABLE_MESSAGE (no response, a timeout, a 502/503/504
 * without a body, the public transport's NETWORK / BAD_RESPONSE). A 4xx or a
 * 5xx the server explained is false — the server is up and answering.
 */
export function isServerUnreachableError(error: unknown): boolean {
  return getErrorMessage(error) === SERVER_UNREACHABLE_MESSAGE;
}

/**
 * Converts an unknown error to a structured AppError
 */
export function toAppError(error: unknown): AppError {
  if (isNetworkError(error)) {
    return {
      code: 'NETWORK_ERROR',
      message: SERVER_UNREACHABLE_MESSAGE,
    };
  }

  if (isUnauthorizedError(error)) {
    return {
      code: 'UNAUTHORIZED',
      message: getErrorMessage(error, 'Authentication required'),
      statusCode: 401,
    };
  }

  if (isForbiddenError(error)) {
    return {
      code: 'FORBIDDEN',
      message: getErrorMessage(error, 'Access denied'),
      statusCode: 403,
    };
  }

  if (isNotFoundError(error)) {
    return {
      code: 'NOT_FOUND',
      message: getErrorMessage(error, 'Resource not found'),
      statusCode: 404,
    };
  }

  if (isRateLimitError(error)) {
    return {
      code: 'RATE_LIMITED',
      message: getErrorMessage(error, 'Too many requests. Please try again later.'),
      statusCode: 429,
    };
  }

  if (isAxiosErrorLike(error) && error.response?.status) {
    const status = error.response.status;
    if (status >= 500) {
      return {
        code: 'SERVER_ERROR',
        message: getErrorMessage(error),
        statusCode: status,
      };
    }
    return {
      code: 'VALIDATION_ERROR',
      message: getErrorMessage(error, 'Invalid request'),
      statusCode: status,
    };
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: getErrorMessage(error),
  };
}
